import { css } from '@emotion/react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { CheckboxComponent } from '~/_shared/baseComponents/checkbox';
import { setModalConfirmedToStorage } from '~/_shared/components/modal/confirmation/confirmationModal.helpers';
import { PermanentConfirmStrategy } from '~/_shared/components/modal/confirmation/useConfirmationModal';
import { ModalComponent } from '~/_shared/components/modal/modal.component';
import { useTranslation } from '~/_shared/utils/hooks';
import { useStorageService } from '~/_shared/utils/storageService';
import { type ModalProps } from '~/modal/modalType.enum';
import {
  clearActiveMapTemplate, closeLeftSidebar,
} from '~/store/frontendState/leftSidebar/leftSidebar.actionCreators';
import { boundaryDrawCloseModal } from '~/store/frontendState/mapTools/boundary/boundaryDraw/boundaryDraw.actionCreators';
import { boundaryFormReset } from '~/store/frontendState/mapTools/boundary/boundaryForm/boundaryForm.actionCreators';
import { boundarySelectEditCloseModal } from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.actionCreators';
import { heatmapFormReset } from '~/store/frontendState/mapTools/heatmap/heatmapForm.actionCreators';
import { frontendStateSearchHide } from '~/store/frontendState/mapTools/search/search.actionCreators';
import { proximityToolReset } from '~/store/frontendState/proximityTool/proximityTool.actionCreators';
import {
  IMAGE_PADDING, IMAGE_WIDTH, MapTemplatesComponent,
} from './mapTemplates.component';
import { MAP_TEMPLATES_AUTO_OPEN_ID } from './mapTemplates.helpers';
import { useMapPageMapTemplatesCallbacks } from './useMapPageMapTemplatesCallbacks.hook';

const IMAGES_PER_ROW = 4;
const CONTENT_PADDING = 24;
export const MAP_TEMPLATES_MODAL_WIDTH = IMAGES_PER_ROW * (IMAGE_WIDTH + IMAGE_PADDING) + CONTENT_PADDING * 2;

export const mapTemplatesModalContentStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
  padding: CONTENT_PADDING,
});

const descriptionRowStyle = css({
  justifyContent: 'space-between',
  display: 'flex',
  width: '100%',
});

export type MapTemplatesProps = ModalProps<{
  isAutoOpened?: boolean;
}>;

export const MapTemplatesContainer = ({ isOpen, isAutoOpened, onClose }: MapTemplatesProps) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const storageService = useStorageService();
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);

  const handleClose = () => {
    if (doNotShowAgain) {
      setModalConfirmedToStorage(storageService, {
        id: MAP_TEMPLATES_AUTO_OPEN_ID,
        strategy: PermanentConfirmStrategy.Local,
      });
    }
    onClose();
  };

  const handleResets = () => {
    dispatch(boundaryDrawCloseModal());
    dispatch(boundaryFormReset());
    dispatch(boundarySelectEditCloseModal());
    dispatch(clearActiveMapTemplate());
    dispatch(frontendStateSearchHide());
    dispatch(heatmapFormReset());
    dispatch(proximityToolReset());
    handleClose();
  };

  const handleCloseAndResets = () => {
    handleResets();
    dispatch(closeLeftSidebar());
  };

  const handleTemplateSelected = useMapPageMapTemplatesCallbacks({
    onSkip: handleCloseAndResets,
    onTemplateSelected: handleResets,
  });

  return (
    <ModalComponent
      contentStyle={mapTemplatesModalContentStyle}
      maxWidth={MAP_TEMPLATES_MODAL_WIDTH}
      caption={t('MapTemplates.caption')}
      isOpen={isOpen}
      onClose={handleClose}
    >
      <div css={descriptionRowStyle}>
        {t('MapTemplates.description')}
        {isAutoOpened && (
          <CheckboxComponent
            isChecked={doNotShowAgain}
            checkedSetter={() => setDoNotShowAgain(prev => !prev)}
            text={t('Do not show again')}
          />
        )}
      </div>
      <MapTemplatesComponent
        onTemplateSelected={handleTemplateSelected}
      />
    </ModalComponent>
  );
};
